import "./styles.scss";

import { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import moment from "moment";
import classnames from "classnames";

import { useTasks } from "context/admin/tasks";

import ModalNew from "components/ModalNew";
import Calendar from "components/Calendar";
import FormikInput from "components/formik/FormikInput";
import FormikSelect from "components/formik/FormikSelect";
import UserPicker from "components/UserPicker";
import FormikSelectablePill from "components/formik/FormikSelectablePill";
import TaskInfo from "components/TaskInfo";
import {
  StatusExplanation,
  PriorityExplanation,
} from "page_components/shared/tasks/explanations";
import FormikTextEditor from "components/formik/FormikTextEditor";
import FormikFilesUpload from "components/formik/FormikFilesUpload";

import useResponsive from "libs/helpers/useResponsive";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";
import createTaskSchema from "libs/validations/admin/createTaskSchema";

const mainClass = "create-task-modal";

const CreateTaskModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const { websites, webmasters, createTask, file_progress } = useTasks();

  const [explanation, setExplanation] = useState(null);

  useEffect(() => {
    if (!isOpen && explanation) setExplanation(null);
  }, [isOpen, explanation]);

  const formatted_websites = websites?.map(({ _id, website }) => ({
    label: website,
    value: _id,
  }));

  const formatted_webmasters = webmasters?.map(
    ({ _id, first_name, last_name, photo }) => ({
      id: _id,
      name: `${first_name} ${last_name}`,
      image: photo,
    }),
  );

  const onSubmit = async (values, setSubmitting) => {
    try {
      await createTask(values);

      onClose();
    } catch (err) {
      console.log(err);
    }

    setSubmitting(false);
  };

  const today = new Date();

  return (
    <>
      <Formik
        initialValues={{
          website: "",
          title: "",
          priority: 2,
          status: 1,
          due_date: null,
          webmaster: [],
          finished_when: "",
          files: [],
        }}
        enableReinitialize
        validationSchema={createTaskSchema}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values, setSubmitting);
        }}
      >
        {({
          isSubmitting,
          submitForm,
          errors,
          values,
          setFieldValue,
          touched,
        }) => (
          <ModalNew
              className="modal-new--create-task"
              title={t("Create task")}
              variant="very-wide"
              labelSubmit={t("Add task")}
              loading={isSubmitting}
              isOpen={isOpen}
              isBackdropDisabled
              onClose={onClose}
              onSubmit={submitForm}
            >
              <Form autoComplete="off">
              <div className={mainClass}>
                <div className={`${mainClass}__col ${mainClass}__col--main`}>
                  <div
                    className={`${mainClass}__field ${mainClass}__field--half-tablet`}
                  >
                    <FormikSelect
                      name="website"
                      label={t("Website [SHORT]")}
                      options={formatted_websites}
                      disabled={isSubmitting}
                      placeholder={t("Select")}
                      searcher
                    />
                  </div>
                  <div
                    className={`${mainClass}__field ${mainClass}__field--half-tablet`}
                  >
                    <FormikInput
                      name="title"
                      label={t("Title")}
                      placeholder={t("Enter")}
                      disabled={isSubmitting}
                    />
                  </div>
                  {max.sm ? (
                    <>
                      <div className={`${mainClass}__field`}>
                        {(() => {
                          const priority = values?.priority;
                          const selected_option = task_priority_data.find(
                            (item) => item.value === priority,
                          );

                          return (
                            <FormikSelect.Priority
                              labelCta={{
                                text: t("Learn more"),
                                onClick: () => setExplanation("priority"),
                              }}
                              name="priority"
                              selected={{
                                value: priority,
                                label: t(selected_option?.label),
                                color: selected_option?.color,
                              }}
                            />
                          );
                        })()}
                      </div>
                      <div className={`${mainClass}__field`}>
                        {(() => {
                          const status = values?.status;
                          const selected_option = task_status_data.find(
                            (item) => item.value === status,
                          );

                          return (
                            <FormikSelect.Status
                              labelCta={{
                                text: t("Learn more"),
                                onClick: () => setExplanation("status"),
                              }}
                              name="status"
                              selected={{
                                value: status,
                                label: t(selected_option?.label),
                                color: selected_option?.color,
                              }}
                              optionFilters={["canceled"]}
                              optionsDisabled={[
                                task_status_data.find(
                                  (item) => item.key === "closed",
                                ).value,
                              ]}
                            />
                          );
                        })()}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className={`${mainClass}__field ${mainClass}__field--flexible ${mainClass}__field--priority`}
                      >
                        <p className={`${mainClass}__field-label`}>
                          {t("Priority")}
                        </p>
                        {(() => {
                          const priority = values?.priority;
                          const selected_option = task_priority_data.find(
                            (item) => item.value === priority,
                          );

                          return (
                            <FormikSelectablePill
                              name="priority"
                              data={{
                                value: priority,
                                label: t(selected_option?.label),
                                color: selected_option?.color,
                              }}
                              icon="flag"
                              options={task_priority_data.map((item) => ({
                                ...item,
                                label: t(item.label),
                              }))}
                              optionsFooter={
                                <TaskInfo
                                  type="priority"
                                  onClick={() => setExplanation("priority")}
                                />
                              }
                              isDisabled={isSubmitting}
                            />
                          );
                        })()}
                      </div>
                      <div
                        className={`${mainClass}__field ${mainClass}__field--flexible ${mainClass}__field--status`}
                      >
                        <p className={`${mainClass}__field-label`}>
                          {t("Status")}
                        </p>
                        {(() => {
                          const status = values?.status;
                          const selected_option = task_status_data.find(
                            (item) => item.value === status,
                          );

                          return (
                            <FormikSelectablePill
                              name="status"
                              data={{
                                value: status,
                                label: t(selected_option?.label),
                                color: selected_option?.color,
                              }}
                              options={task_status_data
                                .filter((item) => item?.key !== "canceled")
                                .map((item) => ({
                                  ...item,
                                  label: t(item.label),
                                }))}
                              optionsDisabled={[
                                task_status_data.find(
                                  (item) => item.key === "closed",
                                ).value,
                              ]}
                              optionsFooter={
                                <TaskInfo
                                  onClick={() => setExplanation("status")}
                                />
                              }
                              isDisabled={isSubmitting}
                            />
                          );
                        })()}
                      </div>
                    </>
                  )}
                  <div
                    className={`${mainClass}__field ${mainClass}__field--row`}
                  >
                    <p className={`${mainClass}__field-label`}>
                      {t("Completion date")}
                    </p>
                    <div
                      className={classnames(`${mainClass}__due-date`, {
                        [`${mainClass}__due-date--error`]:
                          errors?.due_date && touched?.due_date,
                      })}
                    >
                      <p>
                        {values?.due_date
                          ? moment(values.due_date).format("LL")
                          : t("Select date")}
                      </p>
                      <Calendar
                        only_icon
                        label={t("Due date")}
                        default_value={values?.due_date}
                        error={errors?.due_date}
                        prev_date={today}
                        touched={touched}
                        onChange={(value) => setFieldValue("due_date", value)}
                      />
                    </div>
                    {errors?.due_date && touched?.due_date && (
                      <div className={`${mainClass}__error`}>
                        {t(errors.due_date)}
                      </div>
                    )}
                  </div>
                  <div
                    className={`${mainClass}__field ${mainClass}__field--webmasters`}
                  >
                    <p className={`${mainClass}__field-label`}>{t("To")}</p>
                    <UserPicker
                      options={formatted_webmasters}
                      error={
                        errors?.webmaster &&
                        touched?.webmaster &&
                        t(errors.webmaster)
                      }
                      onChange={(selected) =>
                        setFieldValue(
                          "webmaster",
                          selected.map((item) => item.id),
                        )
                      }
                    />
                  </div>
                </div>
                <div
                  className={`${mainClass}__col ${mainClass}__col--description`}
                >
                  <div
                    className={`${mainClass}__field ${mainClass}__field--description`}
                  >
                    <p className={`${mainClass}__field-label`}>
                      {t("Description")}
                    </p>
                    <FormikTextEditor
                      id="finished_when"
                      name="finished_when"
                      placeholder={t(
                        "Add as much information as possible so we can process your request faster.",
                      )}
                      isDisabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className={`${mainClass}__col`}>
                  <div className={`${mainClass}__field`}>
                    <FormikFilesUpload
                      name="files"
                      label={t("Attachments")}
                      progress={file_progress}
                    />
                  </div>
                </div>
              </div>
              </Form>
            </ModalNew>
        )}
      </Formik>

      <StatusExplanation
        isOpen={explanation === "status"}
        onClose={() => setExplanation(null)}
      />
      <PriorityExplanation
        isOpen={explanation === "priority"}
        onClose={() => setExplanation(null)}
      />
    </>
  );
};

export default CreateTaskModal;
